import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { deviceFullInfoReducer } from './deviceFullInfoReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	DeviceFullInfoContext,
	deviceFullInfoInitialState,
	IDataForPushArgs,
	TTypeOfModalWindow,
} from './deviceFullInfoContext';
import { DeviceFullInfoActionTypes } from './deviceFullInfoActions';
import { DevicesContext, IDevicesContext } from 'modules/Devices/Devices/devicesState/devicesContext';
import { DevicesNotificationFileTypes, IGetDeviceInfo, IItemDeviceForGetInfo, TDropboxPushMessage } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDeviceInfo } from 'backend/devices';

const DeviceFullInfoState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(deviceFullInfoReducer, deviceFullInfoInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { deviceIDForFullInfo } = useContext<IDevicesContext>(DevicesContext);
	const { response, refetch, loading } = useFetchData<IGetDeviceInfo, string>(
		deviceIDForFullInfo ? getDeviceInfo : null,
		deviceIDForFullInfo,
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const setDeviceForFullInfo = (value: Nullable<IItemDeviceForGetInfo>) => {
		dispatch({
			type: DeviceFullInfoActionTypes.SET_DEVICE_FOR_FULL_INFO,
			payload: {
				deviceForFullInfo: value,
			},
		});
	};

	const setDropboxPushFileType = useCallback((value: Nullable<DevicesNotificationFileTypes>) => {
		dispatch({
			type: DeviceFullInfoActionTypes.SET_DROPBOX_PUSH_FILE_TYPE,
			payload: {
				selectedDropboxPushFileType: value,
			},
		});
	}, []);

	const setDropboxPushMessage = useCallback((value: Nullable<TDropboxPushMessage>) => {
		dispatch({
			type: DeviceFullInfoActionTypes.SET_DROPBOX_PUSH_MESSAGE,
			payload: {
				selectedDropboxPushMessage: value,
			},
		});
	}, []);

	const changeDropboxPushParams = useCallback((value: string) => {
		dispatch({
			type: DeviceFullInfoActionTypes.CHANGE_DROPBOX_PUSH_PARAMS,
			payload: {
				dropboxPushParams: value,
			},
		});
	}, []);

	const resetDropboxPushFields = () => {
		dispatch({
			type: DeviceFullInfoActionTypes.RESET_DROPBOX_PUSH_FIELDS,
		});
	};

	const resetModalState = () => {
		dispatch({
			type: DeviceFullInfoActionTypes.RESET_MODAL_STATE,
		});
	};

	const setDataForPush = (dataForPush: Nullable<IDataForPushArgs>) => {
		dispatch({
			type: DeviceFullInfoActionTypes.SET_DATA_FOR_PUSH,
			payload: {
				dataForPush,
			},
		});
	};

	const switchBackupEnabled = (value: boolean) => {
		dispatch({
			type: DeviceFullInfoActionTypes.SWITCH_BACKUP_ENABLED,
			payload: {
				isBackupEnabled: value,
				isDeactivateDeviceWithRemoveContent: value ? false : state.isDeactivateDeviceWithRemoveContent,
				isDeactivateDeviceWithResetRestaurant: value ? false : state.isDeactivateDeviceWithResetRestaurant,
			},
		});
	};

	const setDeactivateDeviceWithRemoveContent = () => {
		const isDeactivate = !state.isDeactivateDeviceWithRemoveContent;
		dispatch({
			type: DeviceFullInfoActionTypes.SET_DEACTIVATE_DEVICE_WITH_REMOVE_CONTENT,
			payload: {
				isDeactivateDeviceWithRemoveContent: isDeactivate,
			},
		});
	};

	const setDeactivateDeviceWithResetRestaurant = () => {
		const isDeactivate = !state.isDeactivateDeviceWithResetRestaurant;
		dispatch({
			type: DeviceFullInfoActionTypes.SET_DEACTIVATE_DEVICE_WITH_RESET_RESTAURANT,
			payload: {
				isDeactivateDeviceWithResetRestaurant: isDeactivate,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: DeviceFullInfoActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	useEffect(() => {
		refetch();

		return () => {
			setDeviceForFullInfo(null);
		};
	}, [deviceIDForFullInfo]);

	useEffect(() => {
		response && setDeviceForFullInfo(response.data);
	}, [response]);

	return (
		<DeviceFullInfoContext.Provider
			value={{
				setDeviceForFullInfo,
				setDropboxPushFileType,
				setDropboxPushMessage,
				changeDropboxPushParams,
				resetDropboxPushFields,
				resetModalState,
				setDataForPush,
				switchBackupEnabled,
				setDeactivateDeviceWithRemoveContent,
				setDeactivateDeviceWithResetRestaurant,
				changeTypeOfModalWindow,
				deviceForFullInfo: state.deviceForFullInfo,
				selectedDropboxPushFileType: state.selectedDropboxPushFileType,
				selectedDropboxPushMessage: state.selectedDropboxPushMessage,
				dropboxPushParams: state.dropboxPushParams,
				dataForPush: state.dataForPush,
				isBackupEnabled: state.isBackupEnabled,
				isDeactivateDeviceWithRemoveContent: state.isDeactivateDeviceWithRemoveContent,
				isDeactivateDeviceWithResetRestaurant: state.isDeactivateDeviceWithResetRestaurant,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</DeviceFullInfoContext.Provider>
	);
};

export default DeviceFullInfoState;
